import { useEffect, useLayoutEffect, useMemo, useRef, useState, type DragEvent, type ReactNode } from "react";
import {
  FACTORY_CYCLE_DEFS,
  type FilterType,
  type InstrumentSlot,
  type SamplerLfoWave,
  type SamplerModRoute,
  type SamplerModSource,
  type SamplerModTarget,
} from "../../../../../src/lib/audio";
import {
  useImportedSampleCatalog,
  withCurrentImportedSample,
} from "../../hooks/useImportedSampleCatalog";
import { useSamplerPreset } from "../../hooks/useSamplerPreset";
import { getLabActions } from "../../labActions";
import { fileFromDrop, isSamplerSampleDrag, sampleIdFromDrop, samplerPresetFromSampleId } from "../../lib/sampleDrop";
import { resolveSampleRuntime } from "../../lib/sampleLibrary";
import { loopFromSelection } from "../../lib/wavePeaks";
import { formatOctaveLabel, MusicalKeyboard } from "../MusicalKeyboard";
import { ParamKnob } from "../ParamKnob";
import { SamplerSampleSelect } from "../SamplerSampleSelect";
import { Checkbox } from "../ui/Checkbox";
import { Slider } from "../ui/Slider";
import { instrumentPresetLearnPath } from "../../controls/instrumentPresetLeaves";
import { WaveformThumbnail } from "../wave/WaveformEditor";
import { SamplerBankBar } from "./SamplerBankBar";

const FILTERS: FilterType[] = ["lowpass", "highpass", "bandpass"];
const LFO_WAVES: SamplerLfoWave[] = ["sine", "tri", "square", "saw", "snh", "random"];
const MOD_SOURCES: SamplerModSource[] = [
  "ampEnv", "modEnv", "lfo", "lfo2", "key", "velocity", "random",
];
const MOD_TARGETS: SamplerModTarget[] = [
  "pitch", "cutoff", "amp", "pan", "filterRes",
  "startOffset", "playbackPos", "loopStart", "loopEnd", "sampleRate",
  "grainSize", "grainDensity", "grainSpray", "stretch",
];

/**
 * `kind: "sampler"` dock — SynthEditor density: sections + sliders.
 * Full wave / chop editing lives in Author.
 */
export function SamplerEditor({ slot }: { slot: InstrumentSlot }) {
  const { preset, patch, isSampler } = useSamplerPreset(slot);
  const panelRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [octaveShift, setOctaveShift] = useState(0);
  const [importError, setImportError] = useState<string | null>(null);
  const [thumbPcm, setThumbPcm] = useState<Float32Array | null>(null);
  const [thumbSampleRate, setThumbSampleRate] = useState(48_000);
  const [sampleMissing, setSampleMissing] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const sampleValue = preset?.primarySampleId ?? FACTORY_CYCLE_DEFS[0]!.id;
  const { imported, refresh: refreshImported } = useImportedSampleCatalog();
  const importedOptions = withCurrentImportedSample(
    imported,
    sampleValue,
    preset?.name ?? "Imported",
  );
  const lfo2 = preset?.lfo2 ?? { wave: "square" as SamplerLfoWave, rateHz: 2, depth: 0 };

  useLayoutEffect(() => {
    getLabActions()?.setInstrumentSlot?.(slot);
    setOctaveShift(0);
  }, [slot]);

  useEffect(() => {
    if (preset) {
      void getLabActions()?.ensureSamplerSamples?.(slot);
    }
  }, [slot, preset?.primarySampleId]);

  useEffect(() => {
    let cancelled = false;
    if (!sampleValue) {
      setThumbPcm(null);
      setSampleMissing(false);
      return;
    }
    void resolveSampleRuntime(sampleValue).then(sample => {
      if (!cancelled) {
        setThumbPcm(sample?.pcm ?? null);
        setSampleMissing(!sample);
        if (sample?.sampleRate) {
          setThumbSampleRate(sample.sampleRate);
        }
      }
    });
    return () => {
      cancelled = true;
    };
  }, [sampleValue, imported]);

  const factoryOptions = useMemo(
    () => FACTORY_CYCLE_DEFS.map(d => ({ id: d.id, name: d.name })),
    [],
  );

  if (!isSampler || !preset) {
    return (
      <div className="muted flex min-h-0 flex-1 flex-col overflow-hidden px-3 py-2 text-[12px]">
        Slot is not a Sampler instrument.
      </div>
    );
  }

  return (
    <div
      ref={panelRef}
      className={[
        "flex min-h-0 flex-1 flex-col overflow-hidden outline-none",
        dragOver ? "ring-2 ring-inset ring-lab-accent/50" : "",
      ].join(" ")}
      tabIndex={0}
      onDragOver={event => {
        if (!isSamplerSampleDrag(event as unknown as DragEvent)) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = "copy";
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={async event => {
        event.preventDefault();
        setDragOver(false);
        const libraryId = sampleIdFromDrop(event as unknown as DragEvent);
        if (libraryId) {
          patch(samplerPresetFromSampleId(preset, libraryId));
          void getLabActions()?.ensureSamplerSamples?.(slot);
          refreshImported();
          return;
        }
        const file = fileFromDrop(event as unknown as DragEvent);
        if (!file) return;
        setImportError(null);
        try {
          await getLabActions()?.importSamplerAudio?.(slot, file);
          refreshImported();
        } catch (error) {
          setImportError(error instanceof Error ? error.message : String(error));
        }
      }}
    >
      <SamplerBankBar slot={slot} />
      <div className="flex shrink-0 flex-wrap items-center gap-[0.35rem] border-b border-white/[0.06] bg-lab-panel-bar px-2 py-[0.35rem]">
        <WaveformThumbnail
          pcm={thumbPcm}
          className={`shrink-0${sampleMissing ? " opacity-50 ring-1 ring-rose-400/50" : ""}`}
        />
        <SamplerSampleSelect
          value={sampleValue}
          factory={factoryOptions}
          imported={importedOptions}
          emphasizeMissing={sampleMissing}
          onOpen={() => refreshImported()}
          onChange={sampleId => patch(samplerPresetFromSampleId(preset, sampleId))}
        />
        <button
          type="button"
          className="rounded border border-lab-line bg-transparent px-2 py-1 text-[11px] text-lab-muted hover:bg-white/[0.06] hover:text-slate-200"
          onClick={() => fileRef.current?.click()}
        >
          Import…
        </button>
        <button
          type="button"
          className="rounded border border-lab-line bg-transparent px-2 py-1 text-[11px] text-lab-muted hover:bg-white/[0.06] hover:text-slate-200"
          onClick={() => getLabActions()?.resetInstrumentSlot?.(slot)}
        >
          Reset
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="audio/*,.wav,.mp3,.ogg,.flac,.aif,.aiff"
          className="hidden"
          onChange={async event => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (!file) return;
            setImportError(null);
            try {
              await getLabActions()?.importSamplerAudio?.(slot, file);
              refreshImported();
            } catch (error) {
              setImportError(error instanceof Error ? error.message : String(error));
            }
          }}
        />
      </div>

      {(sampleMissing || importError) && (
        <p className="m-0 shrink-0 border-b border-rose-400/25 bg-rose-500/10 px-2 py-1 text-[11px] text-rose-200">
          {sampleMissing
            ? "Original sample missing — re-import audio or pick another sample."
            : importError}
        </p>
      )}

      <div className="min-h-0 flex-1 overflow-auto">
        <div className="px-[0.6rem] py-2">
          {importError && !sampleMissing && (
            <p className="mb-2 text-[11px] text-red-300">{importError}</p>
          )}

          <Section title="Voice">
            <TextRow
              label="Name"
              value={preset.name}
              onChange={v => patch({ name: v })}
            />
            <SelectRow
              label="Mode"
              value={preset.playMode}
              options={["classic", "granular"]}
              onChange={v => patch({ playMode: v === "granular" ? "granular" : "classic" })}
            />
            {preset.playMode === "classic" && (
              <CheckRow
                label="Keep length"
                checked={preset.preserveDuration === true}
                onChange={preserveDuration => patch({ preserveDuration })}
              />
            )}
            <CheckRow
              label="Loop"
              checked={preset.loop.enabled}
              onChange={enabled => {
                if (enabled) {
                  const end = preset.region.end > preset.region.start
                    ? preset.region.end
                    : Math.max(preset.region.start + 1, thumbPcm?.length ?? preset.loop.end ?? 1);
                  patch({
                    loop: loopFromSelection(
                      { start: preset.region.start, end },
                      thumbSampleRate,
                      preset.loop,
                    ),
                  });
                  return;
                }
                patch({ loop: { ...preset.loop, enabled: false } });
              }}
            />
            {preset.loop.enabled && (
              <SelectRow
                label="Loop exit"
                value={preset.loop.mode ?? "untilRelease"}
                options={["continuous", "untilRelease"]}
                optionLabels={{ continuous: "Continuous", untilRelease: "Until release" }}
                onChange={v =>
                  patch({
                    loop: {
                      ...preset.loop,
                      mode: v === "continuous" ? "continuous" : "untilRelease",
                    },
                  })}
              />
            )}
            <CheckRow
              label="Reverse"
              checked={preset.reverse}
              onChange={reverse => patch({ reverse })}
            />
          </Section>

          <Section title="Filter">
            <SelectRow
              label="Type"
              value={preset.filter.type}
              options={FILTERS}
              onChange={v => patch({ filter: { ...preset.filter, type: v as FilterType } })}
            />
            <SliderRow
              label="Cutoff Hz"
              path="filter.cutoffHz"
              slot={slot}
              min={40}
              max={18000}
              step={1}
              value={preset.filter.cutoffHz}
              onChange={cutoffHz => patch({ filter: { ...preset.filter, cutoffHz } })}
            />
            <SliderRow
              label="Res"
              path="filter.resonance"
              slot={slot}
              min={0}
              max={1}
              step={0.01}
              value={preset.filter.resonance}
              onChange={resonance => patch({ filter: { ...preset.filter, resonance } })}
            />
          </Section>

          <Section title="Amp env">
            <SliderRow label="A" path="ampEnv.a" slot={slot} min={0.001} max={2} step={0.001} value={preset.ampEnv.a} onChange={a => patch({ ampEnv: { ...preset.ampEnv, a } })} />
            <SliderRow label="D" path="ampEnv.d" slot={slot} min={0.001} max={2} step={0.001} value={preset.ampEnv.d} onChange={d => patch({ ampEnv: { ...preset.ampEnv, d } })} />
            <SliderRow label="S" path="ampEnv.s" slot={slot} min={0} max={1} step={0.01} value={preset.ampEnv.s} onChange={s => patch({ ampEnv: { ...preset.ampEnv, s } })} />
            <SliderRow label="R" path="ampEnv.r" slot={slot} min={0.001} max={4} step={0.001} value={preset.ampEnv.r} onChange={r => patch({ ampEnv: { ...preset.ampEnv, r } })} />
          </Section>

          <Section title="Mod env">
            <SliderRow label="A" path="modEnv.a" slot={slot} min={0.001} max={2} step={0.001} value={preset.modEnv.a} onChange={a => patch({ modEnv: { ...preset.modEnv, a } })} />
            <SliderRow label="D" path="modEnv.d" slot={slot} min={0.001} max={2} step={0.001} value={preset.modEnv.d} onChange={d => patch({ modEnv: { ...preset.modEnv, d } })} />
            <SliderRow label="S" path="modEnv.s" slot={slot} min={0} max={1} step={0.01} value={preset.modEnv.s} onChange={s => patch({ modEnv: { ...preset.modEnv, s } })} />
            <SliderRow label="R" path="modEnv.r" slot={slot} min={0.001} max={4} step={0.001} value={preset.modEnv.r} onChange={r => patch({ modEnv: { ...preset.modEnv, r } })} />
          </Section>

          <Section title="LFO 1">
            <SelectRow
              label="Wave"
              value={preset.lfo.wave}
              options={LFO_WAVES}
              onChange={v => patch({ lfo: { ...preset.lfo, wave: v as SamplerLfoWave } })}
            />
            <SliderRow label="Rate Hz" path="lfo.rateHz" slot={slot} min={0.01} max={30} step={0.01} value={preset.lfo.rateHz} onChange={rateHz => patch({ lfo: { ...preset.lfo, rateHz } })} />
            <SliderRow label="Depth" path="lfo.depth" slot={slot} min={0} max={1} step={0.01} value={preset.lfo.depth} onChange={depth => patch({ lfo: { ...preset.lfo, depth } })} />
          </Section>

          <Section title="LFO 2">
            <SelectRow
              label="Wave"
              value={lfo2.wave}
              options={LFO_WAVES}
              onChange={v => patch({ lfo2: { ...lfo2, wave: v as SamplerLfoWave } })}
            />
            <SliderRow label="Rate Hz" path="lfo2.rateHz" slot={slot} min={0.01} max={30} step={0.01} value={lfo2.rateHz} onChange={rateHz => patch({ lfo2: { ...lfo2, rateHz } })} />
            <SliderRow label="Depth" path="lfo2.depth" slot={slot} min={0} max={1} step={0.01} value={lfo2.depth} onChange={depth => patch({ lfo2: { ...lfo2, depth } })} />
          </Section>

          {preset.playMode === "granular" && (
            <Section title="Granular">
              <SliderRow label="Grain ms" path="granular.grainSizeMs" slot={slot} min={5} max={200} step={1} value={preset.granular.grainSizeMs} onChange={grainSizeMs => patch({ granular: { ...preset.granular, grainSizeMs } })} />
              <SliderRow label="Density" path="granular.density" slot={slot} min={0.5} max={32} step={0.1} value={preset.granular.density} onChange={density => patch({ granular: { ...preset.granular, density } })} />
              <SliderRow label="Spray" path="granular.spray" slot={slot} min={0} max={1} step={0.01} value={preset.granular.spray} onChange={spray => patch({ granular: { ...preset.granular, spray } })} />
              <SliderRow label="Stretch" path="granular.stretch" slot={slot} min={0.1} max={4} step={0.01} value={preset.granular.stretch} onChange={stretch => patch({ granular: { ...preset.granular, stretch } })} />
            </Section>
          )}
        </div>

        <ModRoutes
          slot={slot}
          routes={preset.routes}
          onChange={routes => patch({ routes })}
        />
      </div>

      <div className="synth-keys shrink-0 border-t border-white/[0.06] bg-lab-panel-bar px-[0.6rem] pt-1.5 pb-0.5">
        <div className="mb-[0.3rem] flex justify-between text-[10px] tracking-[0.05em] text-lab-muted uppercase">
          <span>Keyboard</span>
          <span className="muted">{formatOctaveLabel(octaveShift)}</span>
        </div>
        <MusicalKeyboard
          key={slot}
          focusHostRef={panelRef}
          baseMidi={60}
          octaves={2}
          voiceMode="poly"
          onNoteOn={(note, velocity) => getLabActions()?.instrumentNoteOn?.(slot, note, velocity)}
          onNoteOff={note => getLabActions()?.instrumentNoteOff?.(slot, note)}
          onOctaveChange={setOctaveShift}
        />
      </div>
      <p className="synth-keys-hint muted m-0 shrink-0 bg-lab-panel-bar px-[0.6rem] pb-2 text-[10px]">
        A–' white · W E T Y U O P black · Z/X octave · Expand for wave editor
      </p>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mb-[0.55rem]">
      <h3 className="mb-[0.3rem] text-[10px] font-semibold tracking-[0.06em] text-lab-muted uppercase">
        {title}
      </h3>
      <div className="grid grid-cols-1 gap-0.5">{children}</div>
    </section>
  );
}

function TextRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className={rowClass}>
      <span>{label}</span>
      <input
        type="text"
        className={controlClass}
        value={value}
        onChange={event => onChange(event.target.value)}
      />
    </label>
  );
}

function SelectRow({
  label,
  value,
  options,
  optionLabels,
  onChange,
}: {
  label: string;
  value: string;
  options: readonly string[];
  optionLabels?: Record<string, string>;
  onChange: (value: string) => void;
}) {
  return (
    <label className={rowClass}>
      <span>{label}</span>
      <select className={controlClass} value={value} onChange={event => onChange(event.target.value)}>
        {options.map(opt => (
          <option key={opt} value={opt}>{optionLabels?.[opt] ?? opt}</option>
        ))}
      </select>
    </label>
  );
}

function CheckRow({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className={rowClass}>
      <span>{label}</span>
      <div className="flex items-center gap-1.5">
        <Checkbox
          checked={checked}
          onCheckedChange={v => onChange(v === true)}
          aria-label={label}
        />
      </div>
      <span className={valClass}>{checked ? "on" : "off"}</span>
    </label>
  );
}

function SliderRow({
  label,
  path,
  slot,
  min,
  max,
  step,
  value,
  onChange,
}: {
  label: string;
  path: string;
  slot: InstrumentSlot;
  min: number;
  max: number;
  step: number;
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <label className={rowClass}>
      <span>{label}</span>
      <Slider
        min={min}
        max={max}
        step={step}
        value={value}
        data-path={path}
        aria-label={label}
        learnPath={instrumentPresetLearnPath(slot, path)}
        onChange={onChange}
      />
      <span className={valClass}>{formatVal(path, value)}</span>
    </label>
  );
}

function ModRoutes({
  slot,
  routes,
  onChange,
}: {
  slot: InstrumentSlot;
  routes: SamplerModRoute[];
  onChange: (routes: SamplerModRoute[]) => void;
}) {
  return (
    <div className="border-t border-white/[0.06] px-[0.6rem] pb-[0.35rem]">
      <div className="my-[0.35rem] mb-1 flex items-center justify-between">
        <h3 className="m-0 text-[10px] font-semibold tracking-[0.06em] text-lab-muted uppercase">
          Mod routes
        </h3>
        <div className="flex items-center gap-1">
          {routes.length > 0 && (
            <button
              type="button"
              className="px-1.5 py-[0.18rem] text-[11px] text-lab-muted"
              onClick={() => onChange([])}
            >
              Clear
            </button>
          )}
          <button
            type="button"
            className="px-1.5 py-[0.18rem] text-[11px]"
            onClick={() =>
              onChange([...routes, { source: "modEnv", target: "cutoff", depth: 1 }])
            }
          >
            + Route
          </button>
        </div>
      </div>
      <div className="flex flex-col gap-0.5">
        {routes.map((route, index) => (
          <div
            key={index}
            className="grid grid-cols-[1fr_1fr_auto_auto_auto] items-center gap-1"
          >
            <select
              className={controlClass}
              value={route.source}
              onChange={event => {
                const next = routes.map((r, i) =>
                  i === index ? { ...r, source: event.target.value as SamplerModSource } : r,
                );
                onChange(next);
              }}
            >
              {MOD_SOURCES.map(s => (
                <option key={s} value={s}>{s === "lfo" ? "lfo1" : s}</option>
              ))}
            </select>
            <select
              className={controlClass}
              value={route.target}
              onChange={event => {
                const next = routes.map((r, i) =>
                  i === index ? { ...r, target: event.target.value as SamplerModTarget } : r,
                );
                onChange(next);
              }}
            >
              {MOD_TARGETS.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <ParamKnob
              min={-2}
              max={2}
              step={0.01}
              value={route.depth}
              defaultValue={1}
              density="compact"
              size={28}
              valuePlacement="end"
              ariaLabel={`Route ${index + 1} depth`}
              learnPath={instrumentPresetLearnPath(slot, `routes.${index}.depth`)}
              onChange={depth => {
                const next = routes.map((r, i) => (i === index ? { ...r, depth } : r));
                onChange(next);
              }}
            />
            <label className="flex items-center gap-[0.28rem] text-[10px] text-lab-muted">
              <Checkbox
                checked={Boolean(route.invert)}
                aria-label={`Route ${index + 1} invert`}
                onCheckedChange={invert => {
                  const next = routes.map((r, i) =>
                    i === index ? { ...r, invert: invert || undefined } : r,
                  );
                  onChange(next);
                }}
              />
              inv
            </label>
            <button
              type="button"
              title="Remove"
              onClick={() => onChange(routes.filter((_, i) => i !== index))}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

const rowClass =
  "grid grid-cols-[4rem_1fr_auto] items-center gap-1 text-[11px] text-lab-muted";
const controlClass =
  "min-w-0 w-full rounded border border-lab-line bg-lab-panel-deep px-[0.28rem] py-[0.18rem] text-slate-200 [font:inherit]";
const valClass =
  "min-w-10 text-right text-[10px] text-slate-300 tabular-nums";

function formatVal(path: string, value: number): string {
  if (path.includes("cutoffHz") || path.includes("grainSizeMs") || path.includes("glideMs")) {
    return String(Math.round(value));
  }
  if (Math.abs(value) >= 10 || Number.isInteger(value)) {
    return String(Math.round(value * 100) / 100);
  }
  return value.toFixed(2);
}
